// Meta Lead → VirtualYard relay (docs/lead-relay.md). Shared wire + domain
// types for the relay service and, later, the CRM screens.
//
// Timestamps cross every boundary as ISO-8601 strings (IsoTimestamp). The one
// exception is `expiresAt` in Firestore, which the store layer writes as a
// Firestore Timestamp because the TTL policy needs that type — it never
// appears on these domain types.

import type { IsoTimestamp } from './common.js';
import type { AssignChoicesEmptyReason } from '../lib/leadRelay/assignment.js';
import type { AnytimeFitnessLeadCapture } from '../lib/leadRelay/anytimefitness.js';
import type { LeadLocale } from '../lib/leadRelay/locale.js';
import type { ModelMapOverrides, ModelMapStandard } from '../lib/leadRelay/modelMapStandard.js';

// ── Providers (2026-10-05, the provider seam) ────────────────────────────────
//
// A PROVIDER is runtime behaviour: how a composed lead becomes a submission.
// Every provider-specific field lives inside that provider's variant of
// `LeadRoute.config`, a DISCRIMINATED UNION on `provider` — never a bag of
// optional fields, because the moment a consumer can write
// `fixed.lid ?? fixed.clubId` the compiler stops helping.
//
// ⚠️ The leadRoutes DOCUMENT is the flat shape (`LeadRouteRecord`, itself a
// union on `provider`); the store layer translates.
//
// ⚠️ `anytimefitness` JOINED in step 3 (2026-10-06) so its routes can be
// CREATED through the setup picker. Its transport is the next step: until
// PROVIDER_CAN_SEND (src/lib/leadRelay/providerVocabulary.ts) flips, an AF route
// cannot be saved active and composeLead refuses it. LeadSentFields is still
// VirtualYard's alone for the same reason.

export const LEAD_PROVIDERS = ['virtualyard', 'anytimefitness'] as const;
export type LeadProvider = (typeof LEAD_PROVIDERS)[number];

export function isLeadProvider(value: unknown): value is LeadProvider {
  return typeof value === 'string' && (LEAD_PROVIDERS as readonly string[]).includes(value);
}

/** The VirtualYard (EV Dealer Group iframe form) variant. */
export interface VirtualYardRouteConfig {
  provider: 'virtualyard';
  /**
   * ⚠️ CARRIES THE DEALER'S `token` AND `key`. Never logged, never written into
   * a submission, never put in an alert (§4.2).
   */
  formUrl: string;
  submitUrlBase: string;
  /** ⚠️ `lid` is the ONLY store-identifying field (§2.2). Do not add a postcode. */
  fixed: { lid: string; employer: string; referralCode: string };
  defaultInterest: string;
  allowedInterests: string[];
  /**
   * The EFFECTIVE map the lead is composed with — the shared standard with this
   * route's overrides applied, resolved by the store when the route is read.
   * Keys normalised (trim, lowercase, collapsed whitespace).
   */
  modelMap: Record<string, string>;
  /** The shared standard this route inherits from (`leadModelMaps/{id}`); null = none. */
  modelMapStandardId: string | null;
  /** ⭐ ONLY this route's departures from the standard — never a whole-map copy (§25). */
  modelMapOverrides: ModelMapOverrides;
  messageTemplate: string;
}

/**
 * The Anytime Fitness variant. ⚠️ EXACTLY ONE club-id field — it writes BOTH
 * location fields of the payload (src/lib/leadRelay/anytimefitness.ts, whose
 * type guard fails to compile if a second is added). The country picks the
 * per-country values (locale, website URL, phone and postcode rules), so no
 * route stores one that could drift from it. ⚠️ No formUrl, no model map, no
 * interests: AF has none of them, and an empty one carried to satisfy a type
 * is exactly what the union exists to prevent.
 */
export interface AnytimeFitnessRouteConfig {
  provider: 'anytimefitness';
  /** AF's club id, e.g. AU-1432. The ONLY club-identifying field. */
  clubId: string;
  country: LeadLocale;
}

/** Discriminated on `provider`. */
export type LeadRouteConfig = VirtualYardRouteConfig | AnytimeFitnessRouteConfig;

/** leadRoutes/{routeId} as the relay uses it — one per store (§9.1). */
export interface LeadRoute {
  id: string;
  name: string;
  accountId: string;
  active: boolean;
  testMode: boolean;
  metaFormNames: string[];
  alertChannel: string | null;
  /** Everything provider-specific. ⚠️ The provider is immutable after create. */
  config: LeadRouteConfig;
}

interface LeadRouteRecordCommon {
  id: string;
  name: string;
  accountId: string;
  active: boolean;
  testMode: boolean;
  metaFormNames: string[];
  alertChannel: string | null;
}

export interface VirtualYardRouteRecord extends LeadRouteRecordCommon {
  provider: 'virtualyard';
  formUrl: string;
  submitUrlBase: string;
  fixed: { lid: string; employer: string; referralCode: string };
  defaultInterest: string;
  allowedInterests: string[];
  /**
   * ⭐ STORE THE DIFF, NOT THE COPY (§25): the standard it inherits from, and
   * only the rows where it departs. The effective map is never stored.
   */
  modelMapStandardId: string | null;
  modelMapOverrides: ModelMapOverrides;
  messageTemplate: string;
}

export interface AnytimeFitnessRouteRecord extends LeadRouteRecordCommon {
  provider: 'anytimefitness';
  clubId: string;
  country: LeadLocale;
}

/**
 * The leadRoutes DOCUMENT shape (minus timestamps), which is also the CRM edit
 * page's wire shape — a union on `provider`, like the config. The store
 * translates between this and `LeadRoute`; nothing else should read it.
 */
export type LeadRouteRecord = VirtualYardRouteRecord | AnytimeFitnessRouteRecord;

/** Meta's lead surface, as stored: fb or ig. Anything else is null. */
export type LeadPlatform = 'fb' | 'ig';

/**
 * The lead, as parsed from the Zap's `rawdata` (§7.1). Every value is a string;
 * absent fields are absent, never `undefined` (Firestore rejects undefined).
 * Empty when the Rawdata was missing or unreadable — the raw body is kept on
 * the submission instead.
 */
export interface LeadInput {
  meta_lead_id?: string;
  form_name?: string;
  campaign_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  postcode?: string;
  model?: string;
  optin?: string;
  /**
   * Meta's `platform` as the stored CODE (platform.ts parseLeadPlatform): `fb`,
   * `ig`, or null for anything else, empty or missing — never a guess. Read
   * through platform.ts's one table for both the Lead Log label and AF's
   * `origin_source`. ⚠️ Empty on a lead no ad served (a direct form test, an
   * organic form) — a real category, not an anomaly.
   */
  platform?: LeadPlatform | null;
  extra?: Record<string, string>;
}

/**
 * The LeadInput keys a person may change on a FAILED lead (logView.ts
 * EDITABLE_LEAD_FIELDS says which providers allow which). A literal union, not
 * `keyof LeadInput`: meta_lead_id / form_name / campaign_name / platform / extra
 * record how the lead ARRIVED, and `optin` is a consent answer — none of them is
 * an answer a person may rewrite.
 */
export type EditableLeadField = 'first_name' | 'last_name' | 'email' | 'phone' | 'postcode' | 'model';

/**
 * Fields a PERSON changed on a failed lead before resending it. Append-only:
 * every edit is a new entry, so the sequence of corrections is readable.
 * ⚠️ `from`/`to` hold the lead's details and live under the submission's own
 * 12-month TTL — they are NEVER audited and never logged.
 * ⚠️ `from: ''` means the lead gave no answer: SUPPLIED by a person, not
 * corrected. The two must read differently on screen.
 */
export interface LeadInputEdit {
  byUid: string;
  byEmail: string;
  at: IsoTimestamp;
  changes: Array<{ field: EditableLeadField; from: string; to: string }>;
}

export type LeadSubmissionKind = 'live' | 'test';

/** Where a DIRECT test lead (one that bypasses the relay and the CRM) was made. */
export type LeadTestOrigin = 'speed_to_lead_settings';

/**
 * received → submitted | retrying → failed, plus test and duplicate (§9.2).
 *
 * `unassigned` (2026-10-07) is a lead whose `account_id` did not pick exactly
 * one active route. It is a NORMAL outcome, not a failure: recorded in full,
 * alerted once, and queued in the Lead Log until a person assigns it — "Assign
 * and send" moves it to `received` and delivers it through the ordinary path.
 * Nothing picks it up automatically (no nextAttemptAt), and it is never sent
 * anywhere until someone chooses its route.
 *
 * `keyed_manually` (§20) is the one status a PERSON sets: a failed lead someone
 * has keyed into VirtualYard by hand. It is terminal, it is never resent, and it
 * stops counting as a failure on the account tile — otherwise the red count
 * could never clear and people would learn to ignore it.
 */
export type LeadSubmissionStatus =
  | 'unassigned'
  | 'received'
  | 'submitted'
  | 'retrying'
  | 'failed'
  | 'keyed_manually'
  | 'test'
  | 'duplicate';

export type LeadFailureReason =
  | 'missing_required'
  /**
   * The lead HAD a route and it went missing or inactive before a retry. A lead
   * that never had a route is `unassigned`, not this.
   */
  | 'no_route'
  | 'form_changed'
  /**
   * The provider answered and refused the lead. Provider-NEUTRAL (2026-10-05):
   * it was `virtualyard_rejected`. Stored docs carrying the old value are
   * deleted by scripts/reset-lead-relay-data.ts; nothing reads it. Never write the
   * old name.
   */
  | 'provider_rejected'
  /**
   * A 2xx the provider's strict response check could not read as a created
   * lead — a challenge page, an empty body, a changed contract. We do not know
   * it landed. Distinct from a clean refusal so it can alert differently.
   */
  | 'provider_unrecognised_response'
  /**
   * Bot protection in front of the provider refused US (AF: Imperva, a 403 or
   * a challenge page under an error status). Nothing about the lead is wrong,
   * and resending into a block changes nothing — not retryable. The fix is a
   * static egress IP the provider allowlists (brief 2026-10-05, Addendum 1).
   */
  | 'provider_blocked'
  /**
   * The provider CREATED the lead, but at a different club than the route's
   * (its echoed location_number differs from the route's club id). Never
   * retried: a retry would create another prospect at the wrong club.
   */
  | 'delivered_wrong_club'
  | 'network'
  | 'blocked_default_interest'
  /**
   * The route's provider cannot send yet (PROVIDER_CAN_SEND) — an Anytime
   * Fitness route before its transport exists. Only a Send test lead can reach
   * this: such a route cannot be saved active.
   */
  | 'provider_not_wired'
  // The route's submit URL is not the provider's pinned host — config, not luck.
  | 'invalid_route';

/**
 * Why `interestedin` is the route default rather than the mapped model (§5.1).
 * null = the model mapped cleanly.
 */
export type LeadInterestFallback =
  | 'none'
  | 'unmapped'
  | 'not_allowed'
  | 'blocked'
  | 'not_on_form';

/**
 * VirtualYard's composed payload — the fields sent to the form.
 * ⚠️ formkey / key / token are NEVER in here.
 */
export interface VirtualYardSentFields {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  postcode: string;
  lid: string;
  interestedin: string;
  employer: string;
  message: string;
  optin: 'yes' | 'no';
}

/**
 * The composed payload, owned by the provider that composed it. Which member a
 * stored `sent` is comes from `LeadSubmission.provider` (2026-10-05) — the
 * payload itself carries no discriminant. Read it through `sentFor` /
 * the provider switch, never by guessing from its keys.
 * ⚠️ Names in either member are stored EMPTY when the form gave none; the
 * transport sends NAME_PLACEHOLDER (names.ts).
 */
export type LeadSentFields = VirtualYardSentFields | AnytimeFitnessLeadCapture;

/**
 * The identifiers Anytime Fitness returns on a successful lead capture — ALL
 * FIVE are stored (addendum 1, the accidental duplicate).
 *
 * ⚠️⚠️ `externalId` IS NOT A PER-LEAD KEY. It identifies the PERSON AT THAT
 * CLUB: two enquiries from one consumer share it (observed — ROX24492 twice).
 * It is the human reconciliation reference, the one to show and quote to AF.
 * `leadId` is the submission's own provider reference — use it wherever a
 * one-to-one key is needed. Never treat externalId as unique.
 */
export interface AnytimeFitnessProviderRef {
  provider: 'anytimefitness';
  /**
   * Null when AF answered without it. ⚠️ Normal for New Zealand (observed
   * 2026-10-28: person_id, lead_capture_id and lead_id, no external_id);
   * contract drift for Australia, where it has always been present.
   */
  externalId: string | null;
  /** The id we hold as THE reference: external_id, else lead_id, else lead_capture_id, else person_id. */
  refId: string;
  /** Which response field `refId` came from. */
  refIdSource: 'external_id' | 'lead_id' | 'lead_capture_id' | 'person_id';
  /** Null only when AF answered without it — reported as contract drift for AU only. */
  leadId: string | null;
  personId: string | null;
  leadCaptureId: string | null;
  trackingId: string | null;
  /**
   * The Anytime app link AF returned (booking relay, 2026-11-03). Used as `deep_link_url` when booking, but
   * AF accepts a booking without it. ⚠️ OPTIONAL: absent on every submission stored before this field existed
   * (those carry the link only inside the stored attempt body), and null when AF answered without it.
   */
  deepLinkUri?: string | null;
  /** AF's `can_book_appointment` for this prospect. Absent on older submissions; null when AF omitted it. */
  canBookAppointment?: boolean | null;
}

/** What a provider handed back about the record it created. VirtualYard returns nothing. */
export type LeadProviderRef = AnytimeFitnessProviderRef;

/**
 * Why the payload itself could not be used (2026-10-07). The payload is
 * `account_id` + `rawdata` and nothing else; a lead whose Rawdata is missing,
 * unreadable or AMBIGUOUS is UNASSIGNED with this reason, its raw body kept for
 * a person. ⚠️ Every one means THERE IS NO LEAD TO SEND, so Assign and send
 * refuses them all.
 *
 * `ambiguous_rawdata` — the body carried more than one key that is `rawdata`
 * once case is ignored (`rawdata` AND `Rawdata`). The relay never picks one.
 */
export type LeadPayloadProblem = 'no_rawdata' | 'rawdata_unreadable' | 'ambiguous_rawdata';

/** What the Rawdata parse found worth telling a person. */
export interface LeadRawdataReport {
  /** Why it did not parse; null when it did. */
  error: string | null;
  /** How many `raw` keys were beyond the cap (MAX_RAW_KEYS) and not read. */
  keysBeyondCap: number;
  /**
   * Top-level keys that are NON-EMPTY, NOT part of Meta's lead envelope
   * (KNOWN_TOP_LEVEL) and NOT in `raw` — a genuinely new field whose value we
   * are losing. ⚠️ Empty on a healthy lead, and that is the point: a notice
   * that fires on every lead teaches people to ignore it.
   */
  unreadTopLevelKeys: string[];
  /**
   * `raw` keys with no top-level twin, whose value fell back to Meta's option
   * key (`not_at_this_stage` instead of "Not at this stage").
   */
  fallbackKeys: string[];
}

/**
 * How the relay resolved a lead's route at receipt (2026-10-07).
 *
 * ⚠️⚠️ `account_id` IS THE ONLY MATCHER. A lead routes if, and only if, its
 * account_id picks exactly one ACTIVE route; every other case is `unassigned`
 * and waits for a person. The form name never routes a lead — it is a
 * SUGGESTION shown to whoever assigns it (`LeadRouteSuggestion`).
 *
 * `routed`                 — exactly one active route belongs to the account.
 * `no_account_id`          — the Zap sent none.
 * `no_active_route`        — sent, but no active route belongs to it. ⚠️ The
 *                            relay does not read accounts (it must not load the
 *                            accounts module), so it cannot tell "an account
 *                            with no route yet" from "no such account"; the
 *                            Lead Log resolves that (`LeadUnassignedReason`).
 * `several_active_routes`  — more than one active route belongs to it; the
 *                            relay never guesses between them.
 * `ambiguous_account_id`   — the body carried more than one key that is
 *                            `account_id` once case is ignored (`account_id` AND
 *                            `ACCOUNT_ID`). Never picked between, even when the
 *                            values agree. ⚠️ Unlike `ambiguous_rawdata` the
 *                            LEAD is fine — only its location is unknown — so
 *                            Assign and send can deliver it.
 */
export type LeadAssignmentStatus =
  | 'routed'
  | 'no_account_id'
  | 'no_active_route'
  | 'several_active_routes'
  | 'ambiguous_account_id';

/**
 * Why a lead is unassigned, as a PERSON reads it — the four cases, exhaustive.
 * The relay's `no_active_route` is split by the Lead Log (which can read
 * accounts) into `unknown_account` (the id matches no account at all) and
 * `no_active_route` (a real account with no active route).
 */
export type LeadUnassignedReason =
  | LeadPayloadProblem
  | 'ambiguous_account_id'
  | 'no_account_id'
  | 'unknown_account'
  | 'no_active_route'
  | 'several_active_routes';

export interface LeadAssertedAccount {
  /** The Zap's account_id, trimmed; null when none was sent. */
  accountId: string | null;
  status: LeadAssignmentStatus;
  /** `several_active_routes` only: every active route on the account, so the fix is obvious. */
  activeRoutes?: Array<{ id: string; name: string }>;
  /** `ambiguous_account_id` only: the keys exactly as they arrived, e.g. ["account_id", "ACCOUNT_ID"]. */
  ambiguousKeys?: string[];
}

/**
 * What the arriving form name would SUGGEST for an unassigned lead — a hint for
 * the person assigning it, never a decision the system made.
 */
export interface LeadRouteSuggestion {
  /** The form name as it arrived; null when none did. */
  formName: string | null;
  /** Routes whose Meta form names list it (normalised), active or not. */
  listedOn: Array<{ id: string; name: string; active: boolean }>;
  /** Fuzzy near-misses, only when no route lists it. */
  closest: Array<{ id: string; name: string }>;
}

/**
 * How a lead's route was decided. `account_id` = automatically, by the one
 * matcher; `hand_assigned` = a person chose it from the Lead Log.
 */
export interface LeadRouting {
  by: 'account_id' | 'hand_assigned';
}

/** Recorded on a lead a person assigned — the audit says the same; this keeps it on the record. */
export interface LeadHandAssignment {
  byUid: string;
  byEmail: string;
  at: IsoTimestamp;
  routeId: string;
  routeName: string;
  /** Why it was unassigned, as the relay recorded it. */
  reason: LeadAssignmentStatus;
  /** What the form-name suggestion said at the moment it was assigned. */
  suggestion: LeadRouteSuggestion;
  /** True when the chosen route is one the suggestion listed the form name on. */
  followedSuggestion: boolean;
  /**
   * The provider a PERSON asserted for a lead whose provider could not be
   * known (the provider picker, 2026-10-07); null when it was known. Recorded
   * and audited with the rest of the assignment.
   */
  assertedProvider: LeadProvider | null;
}

export type LeadNameField = 'first_name' | 'last_name';

/** What name resolution did — see src/lib/leadRelay/names.ts. */
export interface LeadNameResolution {
  /** The full-name key that was split (as found, e.g. `full_name`); null when none was. */
  splitFrom: string | null;
  /** The full name as split; null when none was. */
  fullName: string | null;
  /** The fields filled from the split. */
  derived: LeadNameField[];
  /** The fields that were empty, and so went out as NAME_PLACEHOLDER. */
  placeholder: LeadNameField[];
}

/** One field where a test lead's payload deliberately differed from production. */
export interface LeadTestDifference {
  field: string;
  test: string;
  production: string;
  why: string;
}

export interface LeadAttempt {
  at: IsoTimestamp;
  httpStatus: number | null;
  /** Trimmed and redacted. ⚠️ Can echo lead data — same retention as `input`. */
  responseBody: string | null;
  error: string | null;
  /** 'unknown' = the POST may have landed (a timeout) — see §7.3. */
  outcome: 'ok' | 'failed' | 'unknown';
  reason: LeadFailureReason | null;
}

export interface LeadSubmission {
  id: string;
  kind: LeadSubmissionKind;
  /**
   * Speed to Lead: a TEST lead (kind 'test') that was sent with "also send to Sam", carrying an allowlisted
   * tester's mobile. ONLY this flag lets a test lead through intake; absent/false = a test lead never does.
   */
  intakeTest?: boolean;
  /**
   * Set ONLY on a test lead made from Settings -> Speed to Lead (the DIRECT test lead): it never touched a route,
   * the relay or the CRM provider. Absent on every other submission, so nothing existing changes shape.
   */
  testOrigin?: LeadTestOrigin;
  /** The source a direct test lead was entered with; read by intake (absent = a Meta lead form). */
  testSource?: 'meta_lead_form' | 'website' | 'phone';

  /** DEMO DATA: the seed run id on a synthetic doc. Every outbound path refuses a doc that carries it (src/lib/demoData/demoGuard.ts). Absent on real docs. */
  demoSeed?: string;  routeId: string | null;
  /**
   * The provider that composes and submits this lead — the discriminant for
   * `sent` (2026-10-05). Set from the resolved route; null when no route was
   * ever resolved (an unassigned lead, nothing composed). ⚠️ A doc written
   * before the field existed reads as `virtualyard` (every one of them was);
   * an unrecognised stored value reads as null, never as virtualyard.
   */
  provider: LeadProvider | null;
  /** Denormalised at receipt — `112:2820` alone means nothing six months on. */
  resolvedStore: { name: string; lid: string } | null;
  /** The raw value as received; the doc id is derived from it (§7.2). */
  metaLeadId: string | null;
  metaLeadIdMissing: boolean;
  receivedAt: IsoTimestamp;
  input: LeadInput;
  sent: LeadSentFields | null;
  interestFallback: LeadInterestFallback | null;
  status: LeadSubmissionStatus;
  failureReason: LeadFailureReason | null;
  /** Which required fields were missing, when failureReason is missing_required. */
  missingFields: string[];
  attempts: LeadAttempt[];
  /**
   * The provider's own identifiers for what it created (AF only). ⚠️ OPTIONAL and
   * ABSENT on every VirtualYard submission — no field is written where there is
   * nothing to record, so no existing or VirtualYard document changes shape.
   */
  providerRef?: LeadProviderRef;
  /**
   * ⚠️ How a TEST lead's payload differed from what production sends — e.g.
   * Anytime Fitness test leads send `send_confirmation_emails: false`. Recorded
   * on the test's OWN record so the Lead Log can say it: an undisclosed
   * test/production gap is how a passing test stops meaning anything. Absent
   * on live leads and on any test that matched production exactly.
   */
  testDifferences?: LeadTestDifference[];
  /**
   * How the lead's name was resolved (src/lib/leadRelay/names.ts): a split from
   * a full name, and which name fields went out as NAME_PLACEHOLDER. Written
   * (null when nothing happened) every time the lead is composed; absent on a
   * lead never composed. ⚠️ The placeholder is NEVER stored in `sent` or
   * `input` — this records that it was sent, and to whom is `provider`.
   */
  nameResolution?: LeadNameResolution | null;
  /** Absent on a lead nobody has edited — no field is written where there is nothing to record. */
  inputEdits?: LeadInputEdit[];
  /**
   * Why the payload could not be used; null when it could. A non-null value
   * makes the lead unassigned whatever its account_id says — there is no lead
   * to send — and Assign and send is refused for it.
   */
  payloadProblem: LeadPayloadProblem | null;
  /** What the Rawdata parse reported. Absent when no Rawdata arrived (and on test leads). */
  rawdata?: LeadRawdataReport;
  /**
   * The request body exactly as received, JSON-serialised and capped
   * (RAW_BODY_CAP). What a person reads when the payload could not be used.
   * ⚠️ Holds the lead's personal details — same retention as `input`, never
   * logged, never audited. Absent on test leads.
   * ⚠️⚠️ DELETED when the lead is RESOLVED (`submitted` or `keyed_manually`):
   * it exists for a person to work from when the lead could not be used, and a
   * resolved lead no longer needs a second copy of the customer's details. Kept
   * while unassigned or failed. See store.ts resolvedPatch.
   */
  rawBody?: string;
  /**
   * The location the Zap asserted (`account_id`) and how it resolved — the ONLY
   * matcher. Written on every live lead, including one with no account_id
   * (`status: 'no_account_id'`). Absent on test leads.
   */
  assertedAccount?: LeadAssertedAccount;
  /**
   * How the route was decided. Absent on an unassigned lead (it has none yet)
   * and on test leads (their route is the one the test was sent from).
   */
  routing?: LeadRouting;
  /** Present once a person has assigned the lead (Assign and send). */
  assignment?: LeadHandAssignment;
  /**
   * Index into `attempts` where the current attempt sequence began. Resend
   * starts a new sequence (§7.5) without discarding the history before it.
   */
  attemptSequenceStart: number;
  nextAttemptAt: IsoTimestamp | null;
  leaseUntil: IsoTimestamp | null;
  duplicateOf: string | null;
  resentBy: string | null;
  resentAt: IsoTimestamp | null;
  /** Who marked it keyed in by hand, and when (§20). Null until someone does. */
  keyedManuallyBy: string | null;
  keyedManuallyAt: IsoTimestamp | null;
  /**
   * The keyer's note — REQUIRED when the lead was UNASSIGNED (it had no route,
   * so the note is the only record of where it was keyed and why), optional on
   * a failed lead. Null until someone marks it.
   */
  keyedManuallyNote: string | null;
}

// ── CRM wire shapes (docs/lead-relay.md §10) ─────────────────────────────────

/** One row on the Lead Routes list. ⚠️ No formUrl: the list shows the token TAIL only. */
export interface LeadRouteListItem {
  id: string;
  /** Which system the route sends into — two routes that look alike can behave differently. */
  provider: LeadProvider;
  name: string;
  accountId: string;
  accountName: string | null;
  lid: string;
  metaFormNames: string[];
  active: boolean;
  testMode: boolean;
  /** null for a provider with no form (Anytime Fitness) — not a missing token. */
  formUrlTokenTail: string | null;
  /** The form's host — not secret; groups routes that share a dealer form. */
  formHost: string | null;
  lastLeadReceivedAt: IsoTimestamp | null;
  /** Leads still `received` / `retrying` — they WILL submit even in test mode. */
  inFlightCount: number;
  /**
   * How many rows this route departs from its shared model map — so a diverged
   * route is visible without opening it. null for a provider with no model map.
   */
  modelMapOverrideCount: number | null;
  /** The shared standard's label ("BYD standard"); null when none applies. */
  modelMapStandardLabel: string | null;
}

/** The edit form's payload (admin-only — it carries formUrl). */
export interface LeadRouteDetail {
  /** The stored shape — a union on `provider`. */
  route: LeadRouteRecord;
  /** The standard a VirtualYard route inherits from, so the editor can label every row; null otherwise. */
  modelMapStandard: ModelMapStandard | null;
  formUrlTokenTail: string | null;
  accountName: string | null;
  inFlightCount: number;
}

/** One row in the Lead Log. Staff-visible: it names the lead, by design. */
export interface LeadLogRow {
  id: string;
  kind: LeadSubmissionKind;
  status: LeadSubmissionStatus;
  failureReason: LeadFailureReason | null;
  receivedAt: IsoTimestamp;
  routeId: string | null;
  /** The lead's provider — names its destination ("Store" / "Club"); null while unassigned. */
  provider: LeadProvider | null;
  storeName: string | null;
  lid: string | null;
  leadName: string;
  email: string;
  phone: string;
  attemptCount: number;
  nextAttemptAt: IsoTimestamp | null;
  /** The route's CURRENT test mode, or null when the route is unknown. */
  routeTestMode: boolean | null;
  metaLeadIdMissing: boolean;
  duplicateOf: string | null;
  /** How the route was decided; null while unassigned and on test leads. */
  routedBy: LeadRouting['by'] | null;
  /** Why the lead is unassigned; null unless its status is `unassigned`. */
  unassignedReason: LeadUnassignedReason | null;
  /** Set on a DIRECT test lead (Settings -> Speed to Lead): "Test (direct, not sent to CRM)". Absent otherwise. */
  testOrigin?: LeadTestOrigin;
}

export interface LeadLogResponse {
  rows: LeadLogRow[];
  /** True when the date window held more records than one page returns. */
  truncated: boolean;
  limit: number;
  /**
   * How many records the window holds, counted ONLY when it truncates (a
   * Firestore count over the same query, before the in-memory kind/status
   * filters). null when not truncated — then `rows` is the whole window.
   */
  windowTotal: number | null;
  /**
   * EVERY unassigned lead, whatever the filters and the date window — it is a
   * queue, so the count is shown without anyone applying a filter to find it.
   */
  unassigned: { count: number; oldestReceivedAt: IsoTimestamp | null };
}

/** What a person needs to assign an unassigned lead by hand. */
export interface LeadAssignmentView {
  reason: LeadUnassignedReason;
  accountId: string | null;
  /** ambiguous_account_id only: the keys as they arrived. */
  ambiguousKeys?: string[];
  /** The asserted account's name when it exists in Kyck. */
  accountName: string | null;
  /** Every route on the asserted account, active or not. */
  routesOnAccount: Array<{ id: string; name: string; active: boolean; testMode: boolean }>;
  /** Labelled as a SUGGESTION everywhere it is shown. */
  suggestion: LeadRouteSuggestion;
  /**
   * Routes it can be assigned to (assignment.ts assignableRoutes): on the
   * asserted account where there is one, of the LEAD'S provider, active and not
   * in test mode. The relay refuses anything else with the same rule.
   */
  choices: Array<{ id: string; name: string; accountName: string | null }>;
  /** Why `choices` is empty; null when it is not. */
  choicesEmptyReason: AssignChoicesEmptyReason | null;
  /** The lead's provider as the rule worked it out; null when it could not. */
  leadProvider: LeadProvider | null;
  /**
   * ⚠️ PRESENT ONLY when the provider cannot be known (choicesEmptyReason
   * `provider_unknown`): one entry per provider, each with the routes a person
   * may choose once they assert it. Null otherwise — the picker must never
   * appear for a lead whose provider is known.
   */
  providerAssertion: Array<{
    provider: LeadProvider;
    choices: Array<{ id: string; name: string; accountName: string | null }>;
    emptyReason: AssignChoicesEmptyReason | null;
  }> | null;
}

export interface LeadSubmissionDetail {
  submission: LeadSubmission;
  route: { id: string; name: string; active: boolean; testMode: boolean } | null;
  /** Present only while the lead is unassigned. */
  assignment: LeadAssignmentView | null;
}

/**
 * The account-page tile's data — no lead details at all.
 *
 * ⚠️ `unroutedStores` exists so a store with NO route reads as "not set up"
 * rather than as zero leads (§20). Zero says "the ads aren't working"; a store
 * the relay was never configured for has not measured anything at all.
 */
export interface LeadSummaryResponse {
  routes: Array<{ id: string; name: string; active: boolean; testMode: boolean; provider: LeadProvider }>;
  /** Dealership stores in this account's scope that no route points at. */
  unroutedStores: Array<{ accountId: string; name: string }>;
  leads: Array<{ routeId: string; status: LeadSubmissionStatus; receivedAt: IsoTimestamp }>;
}
